import { useState, useEffect } from 'react';
import { Pause, Play, SkipForward, Calendar, Mail } from 'lucide-react';
import apiClient from '../utils/axios';

export default function SequenceControls({ leadId }) {
  const [enrollment, setEnrollment] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpcoming, setShowUpcoming] = useState(false);

  useEffect(() => {
    if (leadId) {
      fetchEnrollment();
    }
  }, [leadId]);

  const fetchEnrollment = async () => {
    try {
      setLoading(true);
      // Get enrollment for this lead
      const response = await apiClient.get(`/nurture-sequences/enrollments?lead_id=${leadId}`);
      if (response.data.enrollments && response.data.enrollments.length > 0) {
        const enroll = response.data.enrollments[0];
        setEnrollment(enroll);
        
        // Get upcoming emails
        if (enroll.id) {
          fetchUpcoming(enroll.id);
        }
      }
    } catch (error) {
      console.error('Error fetching enrollment:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcoming = async (enrollmentId) => {
    try {
      const response = await apiClient.get(`/nurture-sequences/enrollments/${enrollmentId}/upcoming`);
      setUpcoming(response.data.upcoming || []);
    } catch (error) {
      console.error('Error fetching upcoming:', error);
    }
  };

  const handlePause = async () => {
    if (!enrollment?.id) return;
    
    try {
      await apiClient.post(`/nurture-sequences/enrollments/${enrollment.id}/pause`);
      alert('Sequence paused');
      fetchEnrollment();
    } catch (error) {
      console.error('Error pausing:', error);
      alert('Error pausing sequence: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleResume = async () => {
    if (!enrollment?.id) return;
    
    try {
      await apiClient.post(`/nurture-sequences/enrollments/${enrollment.id}/resume`);
      alert('Sequence resumed');
      fetchEnrollment();
    } catch (error) {
      console.error('Error resuming:', error);
      alert('Error resuming sequence: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSkip = async (stepOrder) => {
    if (!enrollment?.id) return;
    
    if (!confirm(`Skip to step ${stepOrder}?`)) {
      return;
    }

    try {
      await apiClient.post(`/nurture-sequences/enrollments/${enrollment.id}/skip`, {
        step_order: stepOrder
      });
      alert(`Sequence skipped to step ${stepOrder}`);
      fetchEnrollment();
    } catch (error) {
      console.error('Error skipping:', error);
      alert('Error skipping step: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) {
    return <div className="text-xs text-gray-500">Loading sequence...</div>;
  }

  if (!enrollment) {
    return <div className="text-xs text-gray-500">Not in a sequence</div>;
  }

  const isPaused = enrollment.status === 'paused' || enrollment.status === 'paused_replied';

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-600">Sequence:</span>
        <span className="text-xs font-medium text-gray-900">{enrollment.sequence_name || 'Active'}</span>
        <span className="text-xs text-gray-500">Step {enrollment.current_step}</span>
      </div>

      <div className="flex items-center gap-1">
        {isPaused ? (
          <button
            onClick={handleResume}
            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
            title="Resume sequence"
          >
            <Play size={12} />
            Resume
          </button>
        ) : (
          <button
            onClick={handlePause}
            className="px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 flex items-center gap-1"
            title="Pause sequence"
          >
            <Pause size={12} />
            Pause
          </button>
        )}
        
        <button
          onClick={() => setShowUpcoming(!showUpcoming)}
          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
          title="View upcoming emails"
        >
          <Calendar size={12} />
          Upcoming
        </button>
      </div>

      {showUpcoming && upcoming.length > 0 && (
        <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
          <div className="font-medium mb-1">Upcoming Emails:</div>
          {upcoming.map((email) => (
            <div key={email.step_order} className="flex items-center justify-between py-1 border-b border-gray-200 last:border-0">
              <div className="flex-1">
                <div className="font-medium">Step {email.step_order}: {email.step_name}</div>
                <div className="text-gray-600">{email.subject_line}</div>
                <div className="text-gray-500 text-xs">
                  {new Date(email.estimated_send_date).toLocaleDateString()} at {email.optimal_send_time || '09:00'}
                </div>
              </div>
              <button
                onClick={() => handleSkip(email.step_order)}
                className="ml-2 px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-1"
                title="Skip to this step"
              >
                <SkipForward size={10} />
                Skip
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


